import React from 'react'
import { Link } from 'react-router'

import { AlertOctagon, AlertTriangle, Clock } from 'lucide-react'

import type {
  AlertasVencimiento as AlertasVencimientoType,
  AssessmentVencimientoItem
} from '@/types/dashboard_stats.types'

interface AlertasVencimientoProps {
  alertas: AlertasVencimientoType
}

const FASE_LABELS: Record<string, string> = {
  'current-level': 'Nivel actual',
  'gap-analysis': 'Análisis de brechas',
  recommendations: 'Recomendaciones',
  planning: 'Planificación'
}

interface VencimientoItemRowProps {
  item: AssessmentVencimientoItem
  tipo: 'vencido' | 'critico' | 'riesgo'
}

const VencimientoItemRow: React.FC<VencimientoItemRowProps> = ({ item, tipo }) => {
  const styles = {
    vencido: {
      container: 'bg-red-50 border-red-200',
      badge: 'text-red-700',
      icon: <AlertOctagon className="h-3 w-3 text-red-600" />,
      label:
        item.dias_para_vencer < 0
          ? `Venció hace ${Math.abs(item.dias_para_vencer)}d`
          : 'Vencido hoy'
    },
    critico: {
      container: 'bg-orange-50 border-orange-200',
      badge: 'text-orange-700',
      icon: <AlertTriangle className="h-3 w-3 text-orange-600" />,
      label: `Vence en ${item.dias_para_vencer}d`
    },
    riesgo: {
      container: 'bg-yellow-50 border-yellow-200',
      badge: 'text-yellow-700',
      icon: <Clock className="h-3 w-3 text-yellow-600" />,
      label: `Vence en ${item.dias_para_vencer}d`
    }
  }[tipo]

  return (
    <div
      className={`flex items-center justify-between border rounded-lg px-4 py-3 ${styles.container}`}
    >
      <div className="min-w-0">
        <Link
          to={`/assessments/${item.assessment_id}/dashboard`}
          className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate block"
        >
          {item.nombre}
        </Link>
        <p className="text-xs text-gray-500">
          {item.organizacion_nombre} · {FASE_LABELS[item.fase_actual] ?? item.fase_actual}
        </p>
      </div>
      <div
        className={`flex items-center gap-1 ml-4 shrink-0 text-xs font-semibold ${styles.badge}`}
      >
        {styles.icon}
        <span>{styles.label}</span>
      </div>
    </div>
  )
}

export const AlertasVencimiento: React.FC<AlertasVencimientoProps> = ({ alertas }) => {
  const totalAlertas = alertas.vencidos.total + alertas.criticos.total + alertas.en_riesgo.total

  if (totalAlertas === 0) return null

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <AlertOctagon className="h-4 w-4 text-red-600" />
        <h3 className="text-sm font-semibold text-gray-700">Alertas de vencimiento</h3>
        <div className="flex gap-1.5 ml-1">
          {alertas.vencidos.total > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {alertas.vencidos.total} vencido{alertas.vencidos.total > 1 ? 's' : ''}
            </span>
          )}
          {alertas.criticos.total > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              {alertas.criticos.total} crítico{alertas.criticos.total > 1 ? 's' : ''}
            </span>
          )}
          {alertas.en_riesgo.total > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              {alertas.en_riesgo.total} en riesgo
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {alertas.vencidos.items.map((item) => (
          <VencimientoItemRow key={item.assessment_id} item={item} tipo="vencido" />
        ))}
        {alertas.criticos.items.map((item) => (
          <VencimientoItemRow key={item.assessment_id} item={item} tipo="critico" />
        ))}
        {alertas.en_riesgo.items.map((item) => (
          <VencimientoItemRow key={item.assessment_id} item={item} tipo="riesgo" />
        ))}
      </div>
    </div>
  )
}
